import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Building2, Heart, ArrowRight } from "lucide-react";
import api, { fileUrl } from "../../lib/api";
import SeoHead from "../../components/SeoHead";

const TIER_ORDER = ["Donor", "Government", "UN Agency", "Partner", "Implementing Partner", "Other"];

export default function Partners() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/partners").then(({ data }) => setItems(data)).catch(() => {}); }, []);

    const tiers = Array.from(new Set(items.map((p) => p.tier))).sort(
        (a, b) => (TIER_ORDER.indexOf(a) === -1 ? 999 : TIER_ORDER.indexOf(a)) - (TIER_ORDER.indexOf(b) === -1 ? 999 : TIER_ORDER.indexOf(b))
    );
    const featured = items.filter((p) => p.featured);

    return (
        <div data-testid="partners-page">
            <SeoHead pageKey="partners" />
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">Partners & Donors</div>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl mb-6">
                    Walking with us toward resilience.
                </h1>
                <p className="text-lg text-muted-foreground max-w-3xl">
                    YACRRD's work is only possible because of the donors, government bodies, UN agencies, peer NGOs, and grassroots partners who walk alongside us.
                </p>
            </section>

            {featured.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
                    <div className="text-xs uppercase tracking-[0.22em] text-primary mb-6">Featured Partners</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {featured.map((p) => (
                            <PartnerCard key={p.id} partner={p} featured />
                        ))}
                    </div>
                </section>
            )}

            {tiers.map((tier) => {
                const group = items.filter((p) => p.tier === tier);
                if (group.length === 0) return null;
                return (
                    <section key={tier} className="max-w-7xl mx-auto px-6 lg:px-10 py-12 border-t border-border" data-testid={`tier-${tier.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="grid lg:grid-cols-12 gap-10">
                            <div className="lg:col-span-3">
                                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">{tier}</div>
                                <h2 className="font-display text-3xl">{group.length} {group.length === 1 ? "partner" : "partners"}</h2>
                            </div>
                            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {group.map((p) => <PartnerCard key={p.id} partner={p} />)}
                            </div>
                        </div>
                    </section>
                );
            })}

            {items.length === 0 && (
                <div className="text-center text-muted-foreground py-20">No partners listed yet.</div>
            )}

            <section className="bg-primary text-primary-foreground mt-12">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <div className="text-xs uppercase tracking-[0.22em] text-primary-foreground/70 mb-3 inline-flex items-center gap-2"><Heart size={14} /> Become a partner</div>
                        <h2 className="font-display text-4xl mb-2">Co-create resilience with YACRRD.</h2>
                        <p className="text-primary-foreground/80 max-w-2xl">From institutional grants to local volunteering — partnership takes many forms. Tell us how you'd like to engage.</p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-background text-foreground hover:bg-background/90 text-sm font-medium">
                            Start a conversation <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

const PartnerCard = ({ partner, featured }) => {
    const inner = (
        <>
            <div className={`${featured ? "aspect-square" : "aspect-[3/2]"} bg-background border-b border-border flex items-center justify-center p-6`}>
                {partner.logo_url ? (
                    <img src={fileUrl(partner.logo_url)} alt={partner.name} className="max-h-full max-w-full object-contain" />
                ) : (
                    <Building2 size={36} className="text-muted-foreground/40" />
                )}
            </div>
            <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-primary mb-2">{partner.tier}</div>
                <h3 className="font-display text-lg leading-tight mb-1">{partner.name}</h3>
                {partner.description && <p className="text-xs text-muted-foreground line-clamp-2">{partner.description}</p>}
                {partner.website && (
                    <div className="mt-3 text-xs text-primary inline-flex items-center gap-1">
                        Visit <ExternalLink size={11} />
                    </div>
                )}
            </div>
        </>
    );
    const cls = "group block bg-card border border-border rounded-sm overflow-hidden hover:border-primary/60 transition-colors";
    return partner.website ? (
        <a href={partner.website} target="_blank" rel="noreferrer" className={cls} data-testid={`partner-${partner.id}`}>{inner}</a>
    ) : (
        <div className={cls} data-testid={`partner-${partner.id}`}>{inner}</div>
    );
};
